// src/components/CMS/NewsCMSPage.js
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  Autocomplete,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
  Select,
  FormControl,
} from "@mui/material";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import FormatBoldRoundedIcon from "@mui/icons-material/FormatBoldRounded";
import FormatItalicRoundedIcon from "@mui/icons-material/FormatItalicRounded";
import FormatUnderlinedRoundedIcon from "@mui/icons-material/FormatUnderlinedRounded";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import FormatListNumberedRoundedIcon from "@mui/icons-material/FormatListNumberedRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import RedoRoundedIcon from "@mui/icons-material/RedoRounded";
import VerticalAlignBottomRoundedIcon from "@mui/icons-material/VerticalAlignBottomRounded";
import FormatLineSpacingRoundedIcon from "@mui/icons-material/FormatLineSpacingRounded";
import FormatAlignLeftRoundedIcon from "@mui/icons-material/FormatAlignLeftRounded";
import FormatAlignCenterRoundedIcon from "@mui/icons-material/FormatAlignCenterRounded";
import FormatAlignRightRoundedIcon from "@mui/icons-material/FormatAlignRightRounded";
import FormatAlignJustifyRoundedIcon from "@mui/icons-material/FormatAlignJustifyRounded";
import FitScreenRoundedIcon from "@mui/icons-material/FitScreenRounded";
import PhotoRoundedIcon from "@mui/icons-material/PhotoRounded";

import { styled } from "@mui/material/styles";

// TipTap
import {
  useEditor,
  EditorContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import FontFamily from "@tiptap/extension-font-family";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import { Extension } from "@tiptap/core";

// Keep table support (existing content safe)
import {
  Table,
  TableRow,
  TableHeader,
  TableCell,
} from "@tiptap/extension-table";

// ✅ APIs
import {
  apiGetNews,
  apiGetNewsById,
  apiCreateNews,
  apiUpdateNews,
  apiDeleteNews,
  apiGetNewsAssets,
  apiUploadNewsAsset,
  apiDeleteNewsAsset,
  apiGetNewsThumbnail,
  apiUploadNewsThumbnail,
  apiReplaceNewsThumbnail,
  apiDeleteNewsThumbnail,
} from "../../api/News";

const FONT_FAMILY =
  '"LINE Seed TW","Noto Sans TC",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue",Arial,"PingFang TC","Microsoft JhengHei",sans-serif';

const STATUS_OPTIONS = [
  { value: "", label: "全部狀態 / All" },
  { value: "draft", label: "草稿 / Draft" },
  { value: "published", label: "已發布 / Published" },
  { value: "archived", label: "封存 / Archived" },
];

const NEWS_TYPE_OPTIONS = [
  { value: "衛生資訊", label: "衛生資訊" },
  { value: "最新消息", label: "最新消息" },
  { value: "活動資訊", label: "活動資訊" },
];

const FONT_SIZE_OPTIONS = [
  "12px",
  "14px",
  "15px",
  "16px",
  "18px",
  "20px",
  "24px",
  "28px",
  "32px",
];
const LINE_HEIGHT_OPTIONS = ["1", "1.2", "1.4", "1.6", "1.8", "2", "2.4"];
const PARA_SPACING_OPTIONS = [
  "0px",
  "6px",
  "8px",
  "12px",
  "16px",
  "20px",
  "24px",
  "32px",
];

const TEXT_TYPE_OPTIONS = [
  { value: "paragraph", label: "Normal" },
  { value: "h1", label: "Header 1" },
  { value: "h2", label: "Header 2" },
  { value: "h3", label: "Header 3" },
];

const EditorShell = styled(Paper)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  overflow: "hidden",
}));

const ToolbarRow = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: 6,
  alignItems: "center",
  padding: 8,
  borderBottom: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,

  flexWrap: "nowrap",
  overflowX: "auto",
  WebkitOverflowScrolling: "touch",
  "&::-webkit-scrollbar": { display: "none" },

  position: "sticky",
  top: 0,
  zIndex: 2,
}));

const ToolBtn = styled(IconButton)(({ theme, active }) => ({
  borderRadius: 8,
  padding: 6,
  border: `1px solid ${
    active ? theme.palette.primary.main : theme.palette.divider
  }`,
  background: active ? theme.palette.action.selected : "transparent",
}));

const EditorArea = styled(Box)(({ theme }) => ({
  padding: 14,
  fontFamily: FONT_FAMILY,
  "& .tiptap": {
    minHeight: "45vh",
    maxHeight: "70vh",
    overflowY: "auto",
    outline: "none",
    fontFamily: FONT_FAMILY,
  },

  // ✅ allow float-wrapping layouts (text/image beside image)
  "& .tiptap::after": {
    content: '""',
    display: "block",
    clear: "both",
  },

  "& .tiptap p": {
    marginTop: 0,
  },

  "& .tiptap table": {
    width: "100%",
    borderCollapse: "collapse",
    margin: "12px 0",
  },
  "& .tiptap td, & .tiptap th": {
    border: "none",
    verticalAlign: "top",
    padding: "0 10px",
  },
  "& .tiptap tr": { border: "none" },
}));

/* ------------------------------------
   Helpers
------------------------------------ */
const API_BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

function toDateInputValue(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function formatDateDisplay(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString();
}

function formatStatusLabel(v) {
  const s = String(v || "").toLowerCase();
  if (s === "published") return "已發布";
  if (s === "draft") return "草稿";
  if (s === "archived") return "封存";
  return v || "";
}

function isImageMime(mime = "", url = "") {
  if (mime.startsWith("image/")) return true;
  return /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(url || "");
}

function resolveUrlMaybe(relativeOrAbsolute) {
  const u = relativeOrAbsolute || "";
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("blob:") || u.startsWith("data:")) return u;
  if (u.startsWith("//")) return `https:${u}`;
  if (u.startsWith("/")) return `${API_BASE}${u}`;
  return API_BASE ? `${API_BASE}/${u}` : u;
}

function normalizeAsset(raw) {
  if (!raw) return null;
  const url =
    raw.url ||
    raw.fileUrl ||
    raw.publicUrl ||
    raw.path ||
    raw.location ||
    raw.src ||
    "";
  const name =
    raw.originalName || raw.originalname || raw.name || raw.filename || "file";
  const mime = raw.mimeType || raw.mimetype || raw.type || "";
  return { id: raw.id, url: resolveUrlMaybe(url), name, mime, raw };
}

function normalizeHtmlUrls(html) {
  if (!html) return "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("img").forEach((img) => {
      const src = img.getAttribute("src") || "";
      const fixed = resolveUrlMaybe(src);
      if (fixed) img.setAttribute("src", fixed);
    });
    doc.querySelectorAll("a").forEach((a) => {
      const href = a.getAttribute("href") || "";
      const fixed = resolveUrlMaybe(href);
      if (fixed) a.setAttribute("href", fixed);
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
    });
    return doc.body.innerHTML;
  } catch {
    return html;
  }
}

/**
 * ✅ Hashtag parsing (frontend)
 * Supports:
 *  - "#公告 #健康"
 *  - "#公告#健康"
 *  - "#公告　#健康" (fullwidth spaces)
 */
function extractHashtags(text = "") {
  const s = String(text || "");
  const re = /#([^\s#]+)/gu;
  const out = [];
  let m;
  while ((m = re.exec(s))) {
    const tag = String(m[1] || "").trim();
    if (tag) out.push(tag);
  }
  return out;
}

/**
 * Parse a live input string and commit completed tags.
 * Rules:
 * - If user types "#公告 " => commit ["公告"], remaining ""
 * - If user types "#公告#健康" => commit ["公告"], remaining "#健康"
 * - If user types "#公告 #健康" => commit ["公告"], remaining "#健康"
 */
function splitHashtagInput(newValue = "") {
  const s = String(newValue || "");

  if (!s.includes("#")) return { toCommit: [], remaining: s };

  const parts = s.split("#");
  const segs = parts.slice(1);

  if (!segs.length) return { toCommit: [], remaining: s };

  const endsWithSpace = /\s$/u.test(s);

  const committed = [];
  for (let i = 0; i < segs.length; i++) {
    const seg = segs[i];
    const isLast = i === segs.length - 1;

    if (!isLast) {
      const v = String(seg || "").trim();
      if (v) committed.push(v);
      continue;
    }

    if (endsWithSpace) {
      const v = String(seg || "").trim();
      if (v) committed.push(v);
      return { toCommit: committed, remaining: "" };
    }

    const keep = String(seg || "");
    const remaining = keep ? `#${keep}` : "#";
    return { toCommit: committed, remaining };
  }

  return { toCommit: committed, remaining: "" };
}

/* ------------------------------------
   TipTap extensions
------------------------------------ */
const FontSize = Extension.create({
  name: "fontSize",
  addGlobalAttributes() {
    return [
      {
        types: ["textStyle"],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) =>
              element.style.fontSize?.replace(/['"]/g, "") || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },
});

const ParagraphSpacing = Extension.create({
  name: "paragraphSpacing",
  addGlobalAttributes() {
    return [
      {
        types: ["paragraph"],
        attributes: {
          lineHeight: {
            default: null,
            parseHTML: (el) => el.style.lineHeight || null,
            renderHTML: (attrs) =>
              attrs.lineHeight
                ? { style: `line-height:${attrs.lineHeight};` }
                : {},
          },
          marginBottom: {
            default: null,
            parseHTML: (el) => el.style.marginBottom || null,
            renderHTML: (attrs) =>
              attrs.marginBottom
                ? { style: `margin-bottom:${attrs.marginBottom};` }
                : {},
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setParagraphLineHeight:
        (value) =>
        ({ chain }) =>
          chain()
            .setNode("paragraph", { lineHeight: value || null })
            .run(),
      setParagraphSpacing:
        (value) =>
        ({ chain }) =>
          chain()
            .setNode("paragraph", { marginBottom: value || null })
            .run(),
      clearParagraphSpacing:
        () =>
        ({ chain }) =>
          chain()
            .setNode("paragraph", { marginBottom: null, lineHeight: null })
            .run(),
    };
  },
});

function ResizableImageNodeView({
  node,
  updateAttributes,
  selected,
  editor,
  getPos,
}) {
  const startX = useRef(0);
  const startW = useRef(node.attrs.width || 100);

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const onMouseDown = (e, side) => {
    e.preventDefault();
    e.stopPropagation();
    startX.current = e.clientX;
    startW.current = node.attrs.width || 100;

    const onMove = (ev) => {
      const delta = ev.clientX - startX.current;
      const scale = 0.25;
      const newW =
        side === "right"
          ? startW.current + delta * scale
          : startW.current - delta * scale;

      updateAttributes({ width: clamp(newW, 20, 100) });
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // ✅ float-wrapping so text or another image can sit beside it
  const align = node.attrs.align || "center";
  const width = typeof node.attrs.width === "number" ? node.attrs.width : 100;

  const floatStyle =
    align === "left" && width < 100
      ? { float: "left", margin: "10px 12px 10px 0" }
      : align === "right" && width < 100
      ? { float: "right", margin: "10px 0 10px 12px" }
      : { float: "none", margin: "10px auto" };

  return (
    <NodeViewWrapper
      as="div"
      contentEditable={false}
      style={{
        position: "relative",
        width: `${width}%`,
        ...floatStyle,
        outline: selected ? "2px solid #1976d2" : "none",
        borderRadius: 8,
        cursor: "pointer",
        display: "block",
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        try {
          const pos = typeof getPos === "function" ? getPos() : null;
          if (pos != null) editor?.commands?.setNodeSelection?.(pos);
        } catch {}
      }}
    >
      <img
        src={resolveUrlMaybe(node.attrs.src)}
        alt={node.attrs.alt || ""}
        draggable={false}
        style={{
          width: "100%",
          height: "auto",
          display: "block",
          borderRadius: 8,
          userSelect: "none",
        }}
      />

      {selected && (
        <>
          <div
            onMouseDown={(e) => onMouseDown(e, "left")}
            style={{
              position: "absolute",
              left: -6,
              top: "50%",
              width: 12,
              height: 12,
              borderRadius: 999,
              background: "#1976d2",
              transform: "translateY(-50%)",
              cursor: "ew-resize",
            }}
          />
          <div
            onMouseDown={(e) => onMouseDown(e, "right")}
            style={{
              position: "absolute",
              right: -6,
              top: "50%",
              width: 12,
              height: 12,
              borderRadius: 999,
              background: "#1976d2",
              transform: "translateY(-50%)",
              cursor: "ew-resize",
            }}
          />
        </>
      )}
    </NodeViewWrapper>
  );
}

const ImageWithResize = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: { default: 100 },
      align: { default: "center" },
      tempId: { default: null },
      originalName: { default: null },
      mime: { default: null },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageNodeView);
  },
});

/**
 * ✅ Sortable header cell:
 * - Entire cell is clickable (full width/height)
 * - Shows ▲/▼ when active
 */
function SortHeader({
  label,
  columnKey,
  sortBy,
  sortDir,
  onSort,
  sx,
  align = "left",
}) {
  const active = sortBy === columnKey;
  const arrow = active ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  return (
    <Box
      onClick={() => onSort(columnKey)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onSort(columnKey);
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: align === "right" ? "flex-end" : "flex-start",
        minHeight: 34,
        px: 0.5,
        borderRadius: 1.5,
        fontFamily: FONT_FAMILY,
        fontSize: 13,
        fontWeight: 700,
        color: active ? "primary.main" : "text.secondary",
        cursor: "pointer",
        userSelect: "none",
        "&:hover": { color: "primary.main", bgcolor: "action.hover" },
        ...sx,
      }}
    >
      <Box component="span" sx={{ whiteSpace: "nowrap" }}>
        {label}
        {arrow}
      </Box>
    </Box>
  );
}

/* ------------------------------------
   Main component
------------------------------------ */
export default function NewsCMSPage() {
  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingItem, setLoadingItem] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selectedId, setSelectedId] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [newsType, setNewsType] = useState("最新消息");

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [postDate, setPostDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState("draft");

  // ✅ sorting (client-side)
  const [sortBy, setSortBy] = useState("postDate");
  const [sortDir, setSortDir] = useState("desc"); // "asc" | "desc"

  const [content, setContent] = useState("");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const tagDirtyRef = useRef(false);

  const addTags = useCallback((incoming = []) => {
    const cleaned = (Array.isArray(incoming) ? incoming : [])
      .map((s) => String(s || "").trim())
      .map((s) => (s.startsWith("#") ? s.slice(1).trim() : s))
      .filter(Boolean);

    if (!cleaned.length) return;

    setTags((prev) => Array.from(new Set([...(prev || []), ...cleaned])));
  }, []);

  const commitTagInput = useCallback(() => {
    if (!tagDirtyRef.current) return;

    const v = String(tagInput || "");

    const hashtagTags = extractHashtags(v);
    if (hashtagTags.length) {
      addTags(hashtagTags);
      setTagInput("");
      tagDirtyRef.current = false;
      return;
    }

    const single = v.trim();
    if (!single) {
      setTagInput("");
      tagDirtyRef.current = false;
      return;
    }

    addTags([single]);
    setTagInput("");
    tagDirtyRef.current = false;
  }, [tagInput, addTags]);

  const [assets, setAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);

  const [pendingUploads, setPendingUploads] = useState([]);

  const imgInputRef = useRef(null);

  const [thumbnail, setThumbnail] = useState(null);
  const [loadingThumbnail, setLoadingThumbnail] = useState(false);
  const [pendingThumbnail, setPendingThumbnail] = useState(null);
  const thumbInputRef = useRef(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const showSnackbar = useCallback((severity, message) => {
    setSnackbar({ open: true, severity, message });
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const [tick, setTick] = useState(0);
  const bumpTick = useCallback(() => setTick((x) => x + 1), []);

  const fontFamilyOptions = useMemo(() => {
    const fallback = [
      { label: "Default", value: "" },
      {
        label: "LINE Seed TW",
        value: '"LINE Seed TW","Noto Sans TC",system-ui',
      },
      {
        label: "LINE Sans TW",
        value: '"LINE Sans TW","Noto Sans TC",system-ui',
      },
      { label: "Noto Sans TC", value: '"Noto Sans TC",system-ui' },
      { label: "Roboto", value: "Roboto, system-ui, sans-serif" },
      { label: "Arial", value: "Arial, system-ui, sans-serif" },
    ];

    try {
      const ff = typeof document !== "undefined" ? document.fonts : null;
      if (!ff || !ff.values) return fallback;

      const families = new Set();
      for (const f of ff.values()) {
        if (f?.family) families.add(f.family.replace(/['"]/g, "").trim());
      }

      const list = Array.from(families)
        .filter(Boolean)
        .slice(0, 50)
        .map((name) => ({
          label: name,
          value: `"${name}", system-ui, sans-serif`,
        }));

      return [{ label: "Default", value: "" }, ...list];
    } catch {
      return fallback;
    }
  }, []);

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
        Underline,
        TextStyle,
        FontFamily,
        FontSize,
        ParagraphSpacing,
        TextAlign.configure({
          types: ["heading", "paragraph"],
          alignments: ["left", "center", "right", "justify"],
        }),
        Link.configure({ openOnClick: false }),
        Table.configure({
          resizable: false,
          HTMLAttributes: { "data-columns": "true" },
        }),
        TableRow,
        TableHeader,
        TableCell,
        ImageWithResize.configure({
          inline: false,
          allowBase64: true,
        }),
      ],
      content: content || "",
      onUpdate: ({ editor: ed }) => {
        setContent(ed.getHTML());
      },
      editorProps: {
        attributes: {
          class: "tiptap",
          style: `font-family:${FONT_FAMILY};`,
        },
        handleDrop(view, event) {
          const files = Array.from(event?.dataTransfer?.files || []);
          if (!files.length) return false;

          const images = files.filter((f) =>
            (f?.type || "").startsWith("image/")
          );
          if (!images.length) {
            event.preventDefault();
            showSnackbar("warning", "只允許圖片 / Images only");
            return true;
          }

          event.preventDefault();
          return true;
        },
        handlePaste(view, event) {
          const files = Array.from(event?.clipboardData?.files || []);
          if (!files.length) return false;

          const images = files.filter((f) =>
            (f?.type || "").startsWith("image/")
          );
          if (!images.length) {
            showSnackbar("warning", "只允許圖片 / Images only");
            return false;
          }

          event.preventDefault();
          return true;
        },
      },
    },
    []
  );

  useEffect(() => {
    if (!editor) return;
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    editor.commands.setContent(content || "", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    if (!editor) return;
    const onSel = () => bumpTick();
    const onTr = () => bumpTick();
    editor.on("selectionUpdate", onSel);
    editor.on("transaction", onTr);
    return () => {
      editor.off("selectionUpdate", onSel);
      editor.off("transaction", onTr);
    };
  }, [editor, bumpTick]);

  const resetEditor = useCallback(() => {
    setSelectedId(null);
    setNewsType("最新消息");
    setTitle("");
    setSlug("");
    setPostDate("");
    setStartDate("");
    setEndDate("");
    setStatus("draft");
    setTags([]);
    setTagInput("");

    setAssets([]);
    setPendingUploads((prev) => {
      prev.forEach((p) => {
        if (p.previewUrl?.startsWith("blob:"))
          URL.revokeObjectURL(p.previewUrl);
      });
      return [];
    });

    setThumbnail(null);
    setPendingThumbnail((prev) => {
      if (prev?.previewUrl?.startsWith("blob:"))
        URL.revokeObjectURL(prev.previewUrl);
      return null;
    });

    setContent("");
    if (editor) editor.commands.setContent("", false);
  }, [editor]);

  const loadList = useCallback(async () => {
    try {
      setLoadingList(true);
      const data = await apiGetNews({
        q: search.trim() || undefined,
        status: statusFilter || undefined,
        order: "desc",
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("[NewsCMS] loadList error:", err);
      showSnackbar("error", "無法載入新聞列表 / Failed to load news list");
    } finally {
      setLoadingList(false);
    }
  }, [search, statusFilter, showSnackbar]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const loadAssets = useCallback(
    async (newsId) => {
      if (!newsId) {
        setAssets([]);
        return;
      }
      try {
        setLoadingAssets(true);
        const list = await apiGetNewsAssets(newsId);
        const normalized = (Array.isArray(list) ? list : [])
          .map(normalizeAsset)
          .filter(Boolean)
          .filter((a) => isImageMime(a.mime, a.url));
        setAssets(normalized);
      } catch (err) {
        console.error("[NewsCMS] loadAssets error:", err);
        showSnackbar("warning", "附件載入失敗 / Failed to load assets");
      } finally {
        setLoadingAssets(false);
      }
    },
    [showSnackbar]
  );

  const loadThumbnail = useCallback(async (newsId) => {
    if (!newsId) {
      setThumbnail(null);
      return;
    }
    try {
      setLoadingThumbnail(true);
      const data = await apiGetNewsThumbnail(newsId);
      const norm =
        normalizeAsset(data) ||
        (data?.url ? { url: resolveUrlMaybe(data.url) } : null);
      setThumbnail(norm);
    } catch {
      setThumbnail(null);
    } finally {
      setLoadingThumbnail(false);
    }
  }, []);

  const loadItem = useCallback(
    async (id) => {
      if (!id) return;
      try {
        setLoadingItem(true);

        setPendingUploads((prev) => {
          prev.forEach((p) => {
            if (p.previewUrl?.startsWith("blob:"))
              URL.revokeObjectURL(p.previewUrl);
          });
          return [];
        });

        setPendingThumbnail((prev) => {
          if (prev?.previewUrl?.startsWith("blob:"))
            URL.revokeObjectURL(prev.previewUrl);
          return null;
        });

        const data = await apiGetNewsById(id);

        setSelectedId(data.id);
        setNewsType(data.newsType || data.type || "最新消息");

        setTitle(data.title || "");
        setSlug(data.slug || "");
        setPostDate(toDateInputValue(data.postDate || data.date));
        setStartDate(toDateInputValue(data.startDate));
        setEndDate(toDateInputValue(data.endDate));
        setStatus(data.status || "draft");

        const html = normalizeHtmlUrls(data.content || "");
        setContent(html);
        if (editor) editor.commands.setContent(html, false);

        const rawTags = data.tags ?? data.Tags ?? [];
        const tagNames = Array.isArray(rawTags)
          ? rawTags
              .map((t) => (typeof t === "string" ? t : t?.name))
              .filter(Boolean)
          : [];

        setTags(
          Array.from(
            new Set(
              tagNames
                .map((x) => String(x || "").trim())
                .map((x) => (x.startsWith("#") ? x.slice(1).trim() : x))
                .filter(Boolean)
            )
          )
        );

        setTagInput("");

        await Promise.all([loadAssets(data.id), loadThumbnail(data.id)]);
      } catch (err) {
        console.error("[NewsCMS] loadItem error:", err);
        showSnackbar("error", "無法載入新聞內容 / Failed to load news");
      } finally {
        setLoadingItem(false);
      }
    },
    [editor, loadAssets, loadThumbnail, showSnackbar]
  );

  const handleDelete = useCallback(
    async (id) => {
      if (!window.confirm("確定要刪除這則新聞嗎？ / Delete this news?")) return;
      try {
        await apiDeleteNews(id);
        showSnackbar("success", "已刪除 / Deleted");
        if (selectedId === id) resetEditor();
        loadList();
      } catch (err) {
        console.error("[NewsCMS] delete error:", err);
        showSnackbar("error", "刪除失敗 / Failed to delete");
      }
    },
    [selectedId, resetEditor, loadList, showSnackbar]
  );

  const insertImageNode = useCallback(
    ({ src, alt, width = 100, tempId, mime, originalName }) => {
      if (!editor) return;
      editor
        .chain()
        .focus()
        .setImage({
          src,
          alt: alt || "",
          width,
          align: "center",
          tempId: tempId || null,
          mime: mime || null,
          originalName: originalName || null,
        })
        .run();
    },
    [editor]
  );

  const handleInsertImagesReal = useCallback(
    (files) => {
      if (!files?.length) return;

      const newPendings = [];

      files.forEach((file) => {
        const mime = file.type || "";
        if (!mime.startsWith("image/")) return;

        const tempId = `tmp_${Date.now()}_${Math.random()
          .toString(16)
          .slice(2)}`;
        const name = file.name || "image";
        const previewUrl = URL.createObjectURL(file);

        newPendings.push({
          tempId,
          file,
          kind: "image",
          previewUrl,
          name,
          mime,
        });

        insertImageNode({
          src: previewUrl,
          alt: name,
          width: 100,
          tempId,
          mime,
          originalName: name,
        });
      });

      if (newPendings.length) {
        setPendingUploads((prev) => [...prev, ...newPendings]);
        showSnackbar(
          "info",
          `已插入 ${newPendings.length} 張圖片，儲存時會自動上傳 / Inserted ${newPendings.length} image(s), will upload on Save`
        );
      }
    },
    [insertImageNode, showSnackbar]
  );

  const handlePickImage = useCallback(() => {
    imgInputRef.current?.click();
  }, []);

  const onImageInputChange = useCallback(
    (e) => {
      const files = Array.from(e.target.files || []);
      e.target.value = "";
      const images = files.filter((f) => (f?.type || "").startsWith("image/"));
      if (!images.length) {
        showSnackbar("warning", "只允許圖片 / Images only");
        return;
      }
      handleInsertImagesReal(images);
    },
    [handleInsertImagesReal, showSnackbar]
  );

  // ---------------- Thumbnail handlers ----------------
  const handlePickThumbnail = useCallback(() => {
    thumbInputRef.current?.click();
  }, []);

  const onThumbnailInputChange = useCallback(
    (e) => {
      const file = (e.target.files || [])[0];
      e.target.value = "";
      if (!file) return;

      if (!(file.type || "").startsWith("image/")) {
        showSnackbar("warning", "縮圖只允許圖片 / Thumbnail must be an image");
        return;
      }

      setPendingThumbnail((prev) => {
        if (prev?.previewUrl?.startsWith("blob:"))
          URL.revokeObjectURL(prev.previewUrl);
        return null;
      });

      const previewUrl = URL.createObjectURL(file);
      setPendingThumbnail({
        file,
        previewUrl,
        mime: file.type || "",
        name: file.name || "thumbnail",
      });

      showSnackbar(
        "info",
        "已選擇縮圖，儲存時會上傳 / Thumbnail selected, will upload on Save"
      );
    },
    [showSnackbar]
  );

  const handleRemoveThumbnail = useCallback(async () => {
    if (!selectedId) {
      setPendingThumbnail((prev) => {
        if (prev?.previewUrl?.startsWith("blob:"))
          URL.revokeObjectURL(prev.previewUrl);
        return null;
      });
      setThumbnail(null);
      showSnackbar("success", "縮圖已移除 / Thumbnail cleared");
      return;
    }

    if (!window.confirm("確定要移除縮圖嗎？ / Remove thumbnail?")) return;
    try {
      await apiDeleteNewsThumbnail(selectedId);
      setThumbnail(null);
      setPendingThumbnail((prev) => {
        if (prev?.previewUrl?.startsWith("blob:"))
          URL.revokeObjectURL(prev.previewUrl);
        return null;
      });
      showSnackbar("success", "縮圖已移除 / Thumbnail removed");
    } catch (err) {
      console.error("[NewsCMS] delete thumbnail error:", err);
      showSnackbar("error", "移除縮圖失敗 / Failed to remove thumbnail");
    }
  }, [selectedId, showSnackbar]);

  function replaceTempAssetInHtml(html, { tempId, url, name }) {
    if (!tempId || !url) return html;

    let out = html;

    try {
      const doc = new DOMParser().parseFromString(out, "text/html");

      doc.querySelectorAll("img").forEach((img) => {
        const t =
          img.getAttribute("tempId") ||
          img.getAttribute("tempid") ||
          img.getAttribute("data-temp-id");
        if (t === tempId) {
          img.setAttribute("src", resolveUrlMaybe(url));
          if (name) img.setAttribute("alt", name);
          img.removeAttribute("tempId");
          img.removeAttribute("tempid");
          img.removeAttribute("data-temp-id");
        }
      });

      out = doc.body.innerHTML;
    } catch {}

    return out;
  }

  const uploadPendingThenFixContent = useCallback(
    async (newsId, html) => {
      if (!pendingUploads.length) return { html, uploadedCount: 0 };

      let newHtml = html;
      let uploadedCount = 0;

      for (const p of pendingUploads) {
        try {
          const created = await apiUploadNewsAsset(newsId, p.file);
          const norm = normalizeAsset(created);

          if (norm?.url) {
            newHtml = replaceTempAssetInHtml(newHtml, {
              tempId: p.tempId,
              url: norm.url,
              name: norm.name || p.name,
            });
            uploadedCount += 1;
          }
        } catch (err) {
          console.error("[NewsCMS] upload pending failed:", p?.name, err);
        }
      }

      setPendingUploads((prev) => {
        prev.forEach((x) => {
          if (x.previewUrl?.startsWith("blob:"))
            URL.revokeObjectURL(x.previewUrl);
        });
        return [];
      });

      return { html: newHtml, uploadedCount };
    },
    [pendingUploads]
  );

  const uploadPendingThumbnail = useCallback(
    async (newsId) => {
      if (!pendingThumbnail?.file) return { changed: false };

      try {
        const created = await apiReplaceNewsThumbnail(
          newsId,
          pendingThumbnail.file,
          pendingThumbnail.name || "thumbnail"
        );

        const norm =
          normalizeAsset(created) ||
          (created?.url ? { url: resolveUrlMaybe(created.url) } : null);
        if (norm?.url) setThumbnail(norm);

        setPendingThumbnail((prev) => {
          if (prev?.previewUrl?.startsWith("blob:"))
            URL.revokeObjectURL(prev.previewUrl);
          return null;
        });

        return { changed: true };
      } catch (err) {
        console.error("[NewsCMS] upload thumbnail error:", err);

        try {
          const created2 = await apiUploadNewsThumbnail(
            newsId,
            pendingThumbnail.file,
            pendingThumbnail.name || "thumbnail"
          );
          const norm2 =
            normalizeAsset(created2) ||
            (created2?.url ? { url: resolveUrlMaybe(created2.url) } : null);
          if (norm2?.url) setThumbnail(norm2);

          setPendingThumbnail((prev) => {
            if (prev?.previewUrl?.startsWith("blob:"))
              URL.revokeObjectURL(prev.previewUrl);
            return null;
          });

          return { changed: true };
        } catch (err2) {
          console.error("[NewsCMS] upload thumbnail fallback error:", err2);
          showSnackbar("error", "縮圖上傳失敗 / Thumbnail upload failed");
          return { changed: false };
        }
      }
    },
    [pendingThumbnail, showSnackbar]
  );

  const handleSave = useCallback(async () => {
    if (!title.trim()) {
      showSnackbar("error", "標題必填 / Title is required");
      return;
    }
    if (!postDate) {
      showSnackbar("error", "發佈日期必填 / Post date is required");
      return;
    }
    if (!newsType) {
      showSnackbar("error", "新聞類型必填 / News type is required");
      return;
    }
    if (!editor) {
      showSnackbar("error", "Editor not ready");
      return;
    }

    const htmlNow = editor.getHTML();
    if (!htmlNow || !htmlNow.trim() || htmlNow.trim() === "<p></p>") {
      showSnackbar("error", "內容必填 / Content is required");
      return;
    }

    try {
      setSaving(true);

      const slugValue = slug.trim() ? slug.trim() : null;

      const tagsValue = Array.from(
        new Set((tags || []).map((t) => String(t).trim()).filter(Boolean))
      ).map((t) => (t.startsWith("#") ? t.slice(1).trim() : t));

      const basePayload = {
        newsType,
        title: title.trim(),
        content: htmlNow,
        postDate,
        startDate: startDate || null,
        endDate: endDate || null,
        status,
        slug: slugValue,
        tags: tagsValue,
      };

      let id = selectedId;
      if (!id) {
        const created = await apiCreateNews(basePayload);
        id = created?.id;
        setSelectedId(id);
      } else {
        await apiUpdateNews(id, basePayload);
      }

      if (!id) throw new Error("Save failed: missing news id");

      await uploadPendingThumbnail(id);

      const { html: fixedHtml, uploadedCount } =
        await uploadPendingThenFixContent(id, htmlNow);

      if (fixedHtml !== htmlNow) {
        const payload2 = { ...basePayload, content: fixedHtml };
        await apiUpdateNews(id, payload2);

        setContent(fixedHtml);
        editor.commands.setContent(fixedHtml, false);
      }

      await loadList();
      await Promise.all([loadAssets(id), loadThumbnail(id)]);

      showSnackbar(
        "success",
        uploadedCount
          ? `已儲存並上傳 ${uploadedCount} 張圖片 / Saved + uploaded ${uploadedCount}`
          : selectedId
          ? "更新成功 / Updated"
          : "建立成功 / Created"
      );
    } catch (err) {
      console.error("[NewsCMS] save error:", err);
      showSnackbar("error", "儲存失敗 / Failed to save news");
    } finally {
      setSaving(false);
    }
  }, [
    editor,
    title,
    postDate,
    newsType,
    startDate,
    endDate,
    status,
    slug,
    tags,
    selectedId,
    loadList,
    loadAssets,
    loadThumbnail,
    uploadPendingThenFixContent,
    uploadPendingThumbnail,
    showSnackbar,
  ]);

  const insertUploadedAssetIntoEditor = useCallback(
    (asset) => {
      if (!editor || !asset?.url) return;
      insertImageNode({
        src: asset.url,
        alt: asset.name,
        width: 100,
        tempId: null,
        mime: asset.mime,
        originalName: asset.name,
      });
    },
    [editor, insertImageNode]
  );

  const handleDeleteAsset = useCallback(
    async (assetId) => {
      if (!selectedId || !assetId) return;
      if (!window.confirm("確定要刪除此附件嗎？ / Delete this asset?")) return;

      try {
        await apiDeleteNewsAsset(selectedId, assetId);
        showSnackbar("success", "附件已刪除 / Asset deleted");
        await loadAssets(selectedId);
      } catch (err) {
        console.error("[NewsCMS] delete asset error:", err);
        showSnackbar("error", "刪除附件失敗 / Failed to delete asset");
      }
    },
    [selectedId, loadAssets, showSnackbar]
  );

  // ✅ unified align: if image selected => align image (float wrap); else align text
  const setUnifiedAlignValue = useCallback(
    (value) => {
      if (!editor) return;

      if (editor.isActive("image")) {
        const v = value === "justify" ? "center" : value;
        editor.chain().focus().updateAttributes("image", { align: v }).run();
        return;
      }

      editor.chain().focus().setTextAlign(value).run();
    },
    [editor]
  );

  const setImageFullWidth = useCallback(() => {
    if (!editor) return;
    if (!editor.isActive("image")) {
      showSnackbar("warning", "請先選取圖片 / Select an image first");
      return;
    }
    editor
      .chain()
      .focus()
      .updateAttributes("image", { width: 100, align: "center" })
      .run();
  }, [editor, showSnackbar]);

  const setTextTypeValue = useCallback(
    (value) => {
      if (!editor) return;

      if (value === "paragraph") {
        editor.chain().focus().setParagraph().run();
        return;
      }
      if (value === "h1") {
        editor.chain().focus().toggleHeading({ level: 1 }).run();
        return;
      }
      if (value === "h2") {
        editor.chain().focus().toggleHeading({ level: 2 }).run();
        return;
      }
      if (value === "h3") {
        editor.chain().focus().toggleHeading({ level: 3 }).run();
      }
    },
    [editor]
  );

  const setFontFamilyValue = useCallback(
    (value) => {
      if (!editor) return;
      editor
        .chain()
        .focus()
        .setFontFamily(value || null)
        .run();
    },
    [editor]
  );

  const setFontSizeValue = useCallback(
    (value) => {
      if (!editor) return;
      editor
        .chain()
        .focus()
        .setMark("textStyle", { fontSize: value || null })
        .run();
    },
    [editor]
  );

  const setLineHeightValue = useCallback(
    (value) => {
      if (!editor) return;
      editor
        .chain()
        .focus()
        .setParagraphLineHeight(value || null)
        .run();
    },
    [editor]
  );

  const setParaSpacingValue = useCallback(
    (value) => {
      if (!editor) return;
      editor
        .chain()
        .focus()
        .setParagraphSpacing(value || null)
        .run();
    },
    [editor]
  );

  const toolbar = useMemo(() => {
    if (!editor) return null;

    const imgAttrs = editor.getAttributes("image") || {};
    const imgAlign = imgAttrs.align || "center";
    const imgWidth = typeof imgAttrs.width === "number" ? imgAttrs.width : 100;

    const isImg = editor.isActive("image");

    const textType = editor.isActive("heading", { level: 1 })
      ? "h1"
      : editor.isActive("heading", { level: 2 })
      ? "h2"
      : editor.isActive("heading", { level: 3 })
      ? "h3"
      : "paragraph";

    const currentAlign = isImg
      ? imgAlign
      : editor.getAttributes("paragraph")?.textAlign;

    return {
      active: {
        bold: editor.isActive("bold"),
        italic: editor.isActive("italic"),
        underline: editor.isActive("underline"),
        bullet: editor.isActive("bulletList"),
        ordered: editor.isActive("orderedList"),
        link: editor.isActive("link"),

        // ✅ one alignment group (auto targets image or text)
        alignLeft: isImg
          ? imgAlign === "left"
          : editor.isActive({ textAlign: "left" }),
        alignCenter: isImg
          ? imgAlign === "center"
          : editor.isActive({ textAlign: "center" }),
        alignRight: isImg
          ? imgAlign === "right"
          : editor.isActive({ textAlign: "right" }),
        alignJustify: !isImg && editor.isActive({ textAlign: "justify" }),

        imgFull: isImg && imgWidth >= 99,
      },
      currentFont: editor.getAttributes("textStyle")?.fontFamily || "",
      currentSize: editor.getAttributes("textStyle")?.fontSize || "12px",
      currentLH: editor.getAttributes("paragraph")?.lineHeight || "1.6",
      currentMB: editor.getAttributes("paragraph")?.marginBottom || "12px",
      textType,
      isImg,
      currentAlign: currentAlign || "",
    };
  }, [editor, tick]);

  const listIsEmpty = !loadingList && (!rows || rows.length === 0);

  // ✅ click any header cell to sort asc/desc
  const handleSort = useCallback((key) => {
    setSortBy((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("asc");
      return key;
    });
  }, []);

  // ✅ robust per-column value getter (handles backend field differences)
  const STATUS_ORDER = useMemo(
    () => ({ draft: 1, published: 2, archived: 3 }),
    []
  );

  const getSortValue = useCallback(
    (row, key) => {
      if (!row) return null;

      if (key === "title") return row.title || "";
      if (key === "newsType") return row.newsType || row.type || "";
      if (key === "status") {
        const s = String(row.status || "").toLowerCase();
        return STATUS_ORDER[s] ?? 999;
      }

      if (key === "postDate") return row.postDate || row.date || "";
      if (key === "startDate") return row.startDate || "";
      if (key === "endDate") return row.endDate || "";

      return row?.[key];
    },
    [STATUS_ORDER]
  );

  const sortedRows = useMemo(() => {
    if (!rows || !rows.length) return [];

    const dir = sortDir === "asc" ? 1 : -1;

    const isDateKey = (k) =>
      k === "postDate" || k === "startDate" || k === "endDate";

    const toMillis = (v) => {
      if (!v) return 0;
      const t = new Date(v).getTime();
      return Number.isNaN(t) ? 0 : t;
    };

    const normString = (v) => String(v ?? "").toLowerCase();

    return [...rows].sort((a, b) => {
      let va = getSortValue(a, sortBy);
      let vb = getSortValue(b, sortBy);

      if (isDateKey(sortBy)) {
        va = toMillis(va);
        vb = toMillis(vb);
      } else if (sortBy === "status") {
        // numeric order already
        va = Number(va ?? 999);
        vb = Number(vb ?? 999);
      } else {
        // string / fallback
        if (typeof va !== "number") va = normString(va);
        if (typeof vb !== "number") vb = normString(vb);
      }

      if (va > vb) return dir;
      if (va < vb) return -dir;
      return 0;
    });
  }, [rows, sortBy, sortDir, getSortValue]);

  const LIST_VISIBLE_ROWS = 5;
  const ROW_HEIGHT = 42;
  const listMaxHeight = LIST_VISIBLE_ROWS * ROW_HEIGHT;

  const thumbPreviewUrl = pendingThumbnail?.previewUrl || thumbnail?.url || "";

  return (
    <Box sx={{ py: 4, bgcolor: "background.default" }}>
      <Container maxWidth="xl">
        <Typography
          variant="h4"
          sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, mb: 3 }}
        >
          新聞管理
        </Typography>

        <Stack spacing={3}>
          {/* --------------------------- TOP: LIST -------------------------- */}
          <Paper elevation={1} sx={{ p: 2 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", md: "center" }}
              mb={2}
            >
              <TextField
                size="small"
                placeholder="搜尋標題或內容 / Search title or content"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  sx: { fontFamily: FONT_FAMILY },
                  startAdornment: <SearchRoundedIcon fontSize="small" />,
                }}
                fullWidth
              />
              <TextField
                size="small"
                select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ minWidth: { xs: "100%", md: 160 } }}
                InputProps={{ sx: { fontFamily: FONT_FAMILY } }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <Tooltip title="重新整理 / Refresh">
                <span>
                  <IconButton onClick={loadList} disabled={loadingList}>
                    <RefreshRoundedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}
              >
                新聞列表
              </Typography>
              <Button
                size="small"
                startIcon={<AddRoundedIcon />}
                onClick={resetEditor}
                sx={{ fontFamily: FONT_FAMILY }}
              >
                新增
              </Button>
            </Stack>

            <Divider sx={{ mb: 1 }} />

            {/* ✅ UPDATED LIST COLUMNS (ALL sortable) */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr auto",
                  md: "1.6fr 0.8fr 0.7fr 0.9fr 0.9fr 0.9fr auto",
                },
                columnGap: 1,
                mb: 0.5,
              }}
            >
              <SortHeader
                label="標題 / Title"
                columnKey="title"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
              />

              <SortHeader
                label="類型 / Type"
                columnKey="newsType"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                sx={{ display: { xs: "none", md: "flex" } }}
              />

              <SortHeader
                label="狀態 / Status"
                columnKey="status"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                sx={{ display: { xs: "none", md: "flex" } }}
              />

              <SortHeader
                label="發佈日 / Post"
                columnKey="postDate"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                sx={{ display: { xs: "none", md: "flex" } }}
              />

              <SortHeader
                label="開始日 / Start"
                columnKey="startDate"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                sx={{ display: { xs: "none", md: "flex" } }}
              />

              <SortHeader
                label="結束日 / End"
                columnKey="endDate"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                sx={{ display: { xs: "none", md: "flex" } }}
              />

              <Box
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontSize: 13,
                  fontWeight: 700,
                  color: "text.secondary",
                  textAlign: "right",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  minHeight: 34,
                  px: 0.5,
                }}
              >
                操作 / Actions
              </Box>
            </Box>

            <Box sx={{ overflowY: "auto", maxHeight: listMaxHeight }}>
              {loadingList && (
                <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {listIsEmpty && (
                <Box sx={{ py: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: FONT_FAMILY,
                      color: "text.secondary",
                      textAlign: "center",
                    }}
                  >
                    尚未有新聞或搜尋無結果 / No news or no result
                  </Typography>
                </Box>
              )}

              {!loadingList &&
                sortedRows.map((row) => {
                  const typeText = row.newsType || row.type || "";
                  const statusText = formatStatusLabel(row.status);
                  const postText = formatDateDisplay(row.postDate || row.date);
                  const startText = formatDateDisplay(row.startDate);
                  const endText = formatDateDisplay(row.endDate);

                  return (
                    <Box
                      key={row.id}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr auto",
                          md: "1.6fr 0.8fr 0.7fr 0.9fr 0.9fr 0.9fr auto",
                        },
                        columnGap: 1,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        py: 0.75,
                        cursor: "pointer",
                        bgcolor:
                          selectedId === row.id ? "action.hover" : "transparent",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                      onClick={() => loadItem(row.id)}
                    >
                      {/* Title */}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          noWrap
                          sx={{
                            fontFamily: FONT_FAMILY,
                            fontSize: 14,
                            fontWeight: 500,
                          }}
                        >
                          {row.title}
                        </Typography>

                        {/* Mobile meta */}
                        <Typography
                          sx={{
                            display: { xs: "block", md: "none" },
                            mt: 0.25,
                            fontFamily: FONT_FAMILY,
                            fontSize: 12,
                            color: "text.secondary",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {typeText || "-"} ・ {statusText || "-"} ・ Post{" "}
                          {postText || "-"}
                          {startText ? ` ・ Start ${startText}` : ""}
                          {endText ? ` ・ End ${endText}` : ""}
                        </Typography>
                      </Box>

                      {/* Type */}
                      <Box
                        sx={{
                          display: { xs: "none", md: "flex" },
                          alignItems: "center",
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          noWrap
                          sx={{
                            fontFamily: FONT_FAMILY,
                            fontSize: 13,
                            color: "text.secondary",
                          }}
                        >
                          {typeText || "-"}
                        </Typography>
                      </Box>

                      {/* Status */}
                      <Box
                        sx={{
                          display: { xs: "none", md: "flex" },
                          alignItems: "center",
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          noWrap
                          sx={{
                            fontFamily: FONT_FAMILY,
                            fontSize: 13,
                            color: "text.secondary",
                          }}
                        >
                          {statusText || "-"}
                        </Typography>
                      </Box>

                      {/* Post */}
                      <Box
                        sx={{
                          display: { xs: "none", md: "flex" },
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: FONT_FAMILY,
                            fontSize: 13,
                            color: "text.secondary",
                          }}
                        >
                          {postText || "-"}
                        </Typography>
                      </Box>

                      {/* Start */}
                      <Box
                        sx={{
                          display: { xs: "none", md: "flex" },
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: FONT_FAMILY,
                            fontSize: 13,
                            color: "text.secondary",
                          }}
                        >
                          {startText || "-"}
                        </Typography>
                      </Box>

                      {/* End */}
                      <Box
                        sx={{
                          display: { xs: "none", md: "flex" },
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: FONT_FAMILY,
                            fontSize: 13,
                            color: "text.secondary",
                          }}
                        >
                          {endText || "-"}
                        </Typography>
                      </Box>

                      {/* Actions */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                        }}
                      >
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="編輯 / Edit">
                            <span>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  loadItem(row.id);
                                }}
                                color="primary"
                              >
                                <EditRoundedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="刪除 / Delete">
                            <span>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(row.id);
                                }}
                                color="error"
                              >
                                <DeleteRoundedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Box>
                    </Box>
                  );
                })}
            </Box>

            <Typography
              variant="caption"
              sx={{
                fontFamily: FONT_FAMILY,
                color: "text.secondary",
                mt: 1,
                display: "block",
              }}
            >
              顯示高度約 {LIST_VISIBLE_ROWS} 筆，更多請捲動 / Shows ~
              {LIST_VISIBLE_ROWS} rows, scroll for more
            </Typography>
          </Paper>

          {/* --------------------------- BOTTOM: EDITOR ------------------------ */}
          <Paper elevation={1} sx={{ p: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}
              >
                {selectedId
                  ? `編輯新聞 #${selectedId} / Edit News`
                  : "新增新聞 / Create News"}
              </Typography>
              <IconButton size="small" onClick={resetEditor}>
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Stack spacing={1.5}>
              <TextField
                label="標題 / Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                required
                InputLabelProps={{ sx: { fontFamily: FONT_FAMILY } }}
                inputProps={{ sx: { fontFamily: FONT_FAMILY } }}
              />

              {/* ✅ Layout update: newsType + slug + status side-by-side */}
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                alignItems="flex-start"
              >
                <TextField
                  label="類型 / Type"
                  select
                  value={newsType}
                  onChange={(e) => setNewsType(e.target.value)}
                  fullWidth
                  InputLabelProps={{ sx: { fontFamily: FONT_FAMILY } }}
                  InputProps={{ sx: { fontFamily: FONT_FAMILY } }}
                >
                  {NEWS_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="網址代稱 / Slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  fullWidth
                  InputLabelProps={{ sx: { fontFamily: FONT_FAMILY } }}
                  inputProps={{ sx: { fontFamily: FONT_FAMILY } }}
                  helperText="留空則自動從標題產生 / Leave blank to auto-generate from title"
                  required
                />

                <TextField
                  label="狀態 / Status"
                  select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  fullWidth
                  InputLabelProps={{ sx: { fontFamily: FONT_FAMILY } }}
                  InputProps={{ sx: { fontFamily: FONT_FAMILY } }}
                >
                  <MenuItem value="draft">草稿 / Draft</MenuItem>
                  <MenuItem value="published">已發布 / Published</MenuItem>
                  <MenuItem value="archived">封存 / Archived</MenuItem>
                </TextField>
              </Stack>

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                alignItems="flex-start"
              >
                <TextField
                  label="發佈日期 / Post Date"
                  type="date"
                  value={postDate}
                  onChange={(e) => setPostDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  InputProps={{ sx: { fontFamily: FONT_FAMILY } }}
                />
                <TextField
                  label="開始顯示 / Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  InputProps={{ sx: { fontFamily: FONT_FAMILY } }}
                />
                <TextField
                  label="結束顯示 / End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  InputProps={{ sx: { fontFamily: FONT_FAMILY } }}
                />
              </Stack>

              {/* ✅ Tags: use # parsing instead of Enter */}
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={tags}
                inputValue={tagInput}
                onInputChange={(e, newInputValue) => {
                  if (typeof newInputValue === "string")
                    tagDirtyRef.current = true;

                  const { toCommit, remaining } =
                    splitHashtagInput(newInputValue);
                  if (toCommit.length) addTags(toCommit);

                  setTagInput(remaining);
                }}
                onChange={(e, newValue) => {
                  const cleaned = (newValue || [])
                    .map((v) =>
                      typeof v === "string" ? v : v?.name || v?.label || ""
                    )
                    .map((s) => String(s).trim())
                    .map((s) => (s.startsWith("#") ? s.slice(1).trim() : s))
                    .filter(Boolean);

                  setTags(Array.from(new Set(cleaned)));

                  setTagInput("");
                  tagDirtyRef.current = false;
                }}
                onBlur={() => commitTagInput()}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      sx={{ fontFamily: FONT_FAMILY }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="標籤 / Tags"
                    placeholder="用 # 分隔，例如：#公告 #健康 或 #公告#健康"
                    InputLabelProps={{ sx: { fontFamily: FONT_FAMILY } }}
                    helperText="⚠️ 只會以 # 開頭才會被視為標籤。輸入 #公告#健康 或 #公告 #健康 即可。"
                    inputProps={{
                      ...params.inputProps,
                      sx: { fontFamily: FONT_FAMILY },
                      onKeyDown: (ev) => {
                        if (ev.key === "Enter") ev.preventDefault();
                      },
                    }}
                  />
                )}
              />

              {/* ------------------- Thumbnail ------------------- */}
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.75 }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontFamily: FONT_FAMILY, fontWeight: 700 }}
                  >
                    縮圖 / Thumbnail
                  </Typography>

                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PhotoRoundedIcon />}
                      onClick={handlePickThumbnail}
                      sx={{ fontFamily: FONT_FAMILY }}
                      disabled={saving}
                    >
                      上傳並設為縮圖
                    </Button>

                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      onClick={handleRemoveThumbnail}
                      sx={{ fontFamily: FONT_FAMILY }}
                      disabled={saving || (!thumbPreviewUrl && !pendingThumbnail)}
                    >
                      移除縮圖
                    </Button>
                  </Stack>
                </Stack>

                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontFamily: FONT_FAMILY,
                    color: "text.secondary",
                    mb: 1,
                  }}
                >
                  用於 News 列表卡片顯示的封面圖（建議 16:9）。選擇後「儲存」時會上傳並套用。
                </Typography>

                <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                  {loadingThumbnail ? (
                    <Box
                      sx={{ py: 2, display: "flex", justifyContent: "center" }}
                    >
                      <CircularProgress size={22} />
                    </Box>
                  ) : thumbPreviewUrl ? (
                    <Box
                      sx={{
                        position: "relative",
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Box
                        component="img"
                        src={thumbPreviewUrl}
                        alt="thumbnail"
                        sx={{
                          width: "100%",
                          maxHeight: 240,
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                      {pendingThumbnail && (
                        <Box
                          sx={{
                            position: "absolute",
                            left: 10,
                            bottom: 10,
                            px: 1,
                            py: 0.5,
                            borderRadius: 999,
                            bgcolor: "rgba(0,0,0,0.55)",
                            color: "white",
                            fontSize: 12,
                            fontFamily: FONT_FAMILY,
                          }}
                        >
                          尚未儲存 / Pending
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: "text.secondary",
                        textAlign: "center",
                        py: 2.5,
                      }}
                    >
                      尚未設定縮圖 / No thumbnail yet
                    </Typography>
                  )}
                </Paper>

                <input
                  ref={thumbInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={onThumbnailInputChange}
                />
              </Box>

              {/* ------------------- Editor ------------------- */}
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontFamily: FONT_FAMILY, fontWeight: 700 }}
                  >
                    內容編輯 / Editor
                  </Typography>

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ImageRoundedIcon />}
                    onClick={handlePickImage}
                    sx={{ fontFamily: FONT_FAMILY }}
                    disabled={!editor}
                  >
                    插入圖片
                  </Button>
                </Stack>

                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontFamily: FONT_FAMILY,
                    color: "text.secondary",
                    mb: 1,
                  }}
                >
                  ✅ 只允許圖片：可拖曳/貼上圖片到編輯器任意位置（或點上方按鈕）。儲存時會自動上傳並替換成正式連結。
                  <br />
                  ✅ 圖片要「旁邊有文字/圖片」：先把圖片縮小（拖曳左右圓點），再按靠左/靠右（會變成浮動排列）。
                </Typography>

                <EditorShell variant="outlined">
                  <ToolbarRow>
                    {/* ✅ Text type (Normal / H1 / H2 / H3) */}
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <Select
                        value={toolbar?.textType || "paragraph"}
                        onMouseDown={(e) => e.preventDefault()}
                        onChange={(e) => setTextTypeValue(e.target.value)}
                        sx={{ fontFamily: FONT_FAMILY, borderRadius: 2 }}
                      >
                        {TEXT_TYPE_OPTIONS.map((o) => (
                          <MenuItem
                            key={o.value}
                            value={o.value}
                            sx={{ fontFamily: FONT_FAMILY }}
                          >
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl
                      size="small"
                      sx={{ minWidth: { xs: 140, md: 220 } }}
                    >
                      <Select
                        value={toolbar?.currentFont || ""}
                        displayEmpty
                        onMouseDown={(e) => e.preventDefault()}
                        onChange={(e) => setFontFamilyValue(e.target.value)}
                        sx={{ fontFamily: FONT_FAMILY, borderRadius: 2 }}
                      >
                        {fontFamilyOptions.map((f) => (
                          <MenuItem
                            key={`${f.label}-${f.value}`}
                            value={f.value}
                            sx={{ fontFamily: FONT_FAMILY }}
                          >
                            {f.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl
                      size="small"
                      sx={{ minWidth: { xs: 90, md: 120 } }}
                    >
                      <Select
                        value={toolbar?.currentSize || "12px"}
                        onMouseDown={(e) => e.preventDefault()}
                        onChange={(e) => setFontSizeValue(e.target.value)}
                        sx={{ fontFamily: FONT_FAMILY, borderRadius: 2 }}
                      >
                        {FONT_SIZE_OPTIONS.map((s) => (
                          <MenuItem
                            key={s}
                            value={s}
                            sx={{ fontFamily: FONT_FAMILY }}
                          >
                            {s}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Divider flexItem orientation="vertical" />

                    <Tooltip title="Bold">
                      <ToolBtn
                        active={toolbar?.active.bold ? 1 : 0}
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        disabled={!editor.can().chain().focus().toggleBold().run()}
                      >
                        <FormatBoldRoundedIcon fontSize="small" />
                      </ToolBtn>
                    </Tooltip>

                    <Tooltip title="Italic">
                      <ToolBtn
                        active={toolbar?.active.italic ? 1 : 0}
                        onClick={() =>
                          editor.chain().focus().toggleItalic().run()
                        }
                        disabled={
                          !editor.can().chain().focus().toggleItalic().run()
                        }
                      >
                        <FormatItalicRoundedIcon fontSize="small" />
                      </ToolBtn>
                    </Tooltip>

                    <Tooltip title="Underline">
                      <ToolBtn
                        active={toolbar?.active.underline ? 1 : 0}
                        onClick={() =>
                          editor.chain().focus().toggleUnderline().run()
                        }
                      >
                        <FormatUnderlinedRoundedIcon fontSize="small" />
                      </ToolBtn>
                    </Tooltip>

                    <Divider flexItem orientation="vertical" />

                    <Tooltip title="Bulleted list">
                      <ToolBtn
                        active={toolbar?.active.bullet ? 1 : 0}
                        onClick={() =>
                          editor.chain().focus().toggleBulletList().run()
                        }
                      >
                        <FormatListBulletedRoundedIcon fontSize="small" />
                      </ToolBtn>
                    </Tooltip>

                    <Tooltip title="Numbered list">
                      <ToolBtn
                        active={toolbar?.active.ordered ? 1 : 0}
                        onClick={() =>
                          editor.chain().focus().toggleOrderedList().run()
                        }
                      >
                        <FormatListNumberedRoundedIcon fontSize="small" />
                      </ToolBtn>
                    </Tooltip>

                    <Divider flexItem orientation="vertical" />

                    <Tooltip title="Insert/Edit link">
                      <ToolBtn
                        active={toolbar?.active.link ? 1 : 0}
                        onClick={() => {
                          const prev = editor.getAttributes("link").href || "";
                          const url = window.prompt("URL", prev);
                          if (url === null) return;
                          if (url === "") {
                            editor.chain().focus().unsetLink().run();
                            return;
                          }
                          editor.chain().focus().setLink({ href: url }).run();
                        }}
                      >
                        <LinkRoundedIcon fontSize="small" />
                      </ToolBtn>
                    </Tooltip>

                    <Divider flexItem orientation="vertical" />

                    <Tooltip title="Line height (selected paragraphs)">
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <FormatLineSpacingRoundedIcon fontSize="small" />
                        <FormControl size="small" sx={{ minWidth: 90 }}>
                          <Select
                            value={toolbar?.currentLH || "1.6"}
                            onMouseDown={(e) => e.preventDefault()}
                            onChange={(e) => setLineHeightValue(e.target.value)}
                            sx={{ fontFamily: FONT_FAMILY, borderRadius: 2 }}
                          >
                            {LINE_HEIGHT_OPTIONS.map((v) => (
                              <MenuItem
                                key={v}
                                value={v}
                                sx={{ fontFamily: FONT_FAMILY }}
                              >
                                {v}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Tooltip>

                    <Tooltip title="Paragraph spacing bottom (selected paragraphs)">
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <VerticalAlignBottomRoundedIcon fontSize="small" />
                        <FormControl size="small" sx={{ minWidth: 110 }}>
                          <Select
                            value={toolbar?.currentMB || "12px"}
                            onMouseDown={(e) => e.preventDefault()}
                            onChange={(e) => setParaSpacingValue(e.target.value)}
                            sx={{ fontFamily: FONT_FAMILY, borderRadius: 2 }}
                          >
                            {PARA_SPACING_OPTIONS.map((v) => (
                              <MenuItem
                                key={v}
                                value={v}
                                sx={{ fontFamily: FONT_FAMILY }}
                              >
                                {v}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Tooltip>

                    <Divider flexItem orientation="vertical" />

                    {/* ✅ ONE alignment group for both text + image */}
                    <Tooltip
                      title={toolbar?.isImg ? "Image float left" : "Text align left"}
                    >
                      <ToolBtn
                        active={toolbar?.active.alignLeft ? 1 : 0}
                        onClick={() => setUnifiedAlignValue("left")}
                      >
                        <FormatAlignLeftRoundedIcon fontSize="small" />
                      </ToolBtn>
                    </Tooltip>
                    <Tooltip
                      title={toolbar?.isImg ? "Image center" : "Text align center"}
                    >
                      <ToolBtn
                        active={toolbar?.active.alignCenter ? 1 : 0}
                        onClick={() => setUnifiedAlignValue("center")}
                      >
                        <FormatAlignCenterRoundedIcon fontSize="small" />
                      </ToolBtn>
                    </Tooltip>
                    <Tooltip
                      title={toolbar?.isImg ? "Image float right" : "Text align right"}
                    >
                      <ToolBtn
                        active={toolbar?.active.alignRight ? 1 : 0}
                        onClick={() => setUnifiedAlignValue("right")}
                      >
                        <FormatAlignRightRoundedIcon fontSize="small" />
                      </ToolBtn>
                    </Tooltip>
                    <Tooltip title="Text justify">
                      <ToolBtn
                        active={toolbar?.active.alignJustify ? 1 : 0}
                        onClick={() => setUnifiedAlignValue("justify")}
                        disabled={toolbar?.isImg}
                      >
                        <FormatAlignJustifyRoundedIcon fontSize="small" />
                      </ToolBtn>
                    </Tooltip>

                    <Tooltip title="Image full width (select image)">
                      <ToolBtn
                        active={toolbar?.active.imgFull ? 1 : 0}
                        onClick={setImageFullWidth}
                      >
                        <FitScreenRoundedIcon fontSize="small" />
                      </ToolBtn>
                    </Tooltip>

                    <Divider flexItem orientation="vertical" />

                    <Tooltip title="Undo">
                      <ToolBtn
                        active={0}
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={!editor.can().chain().focus().undo().run()}
                      >
                        <UndoRoundedIcon fontSize="small" />
                      </ToolBtn>
                    </Tooltip>
                    <Tooltip title="Redo">
                      <ToolBtn
                        active={0}
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={!editor.can().chain().focus().redo().run()}
                      >
                        <RedoRoundedIcon fontSize="small" />
                      </ToolBtn>
                    </Tooltip>
                  </ToolbarRow>

                  <EditorArea>
                    <EditorContent editor={editor} />
                  </EditorArea>
                </EditorShell>

                <input
                  ref={imgInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={onImageInputChange}
                />
              </Box>

              {/* ------------------- Uploaded assets panel ------------------- */}
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}
                  >
                    已上傳附件 / Uploaded Assets
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: FONT_FAMILY, color: "text.secondary" }}
                  >
                    上傳會在儲存時發生 / Upload happens on Save
                  </Typography>
                </Stack>

                <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                  {loadingAssets ? (
                    <Box
                      sx={{ py: 2, display: "flex", justifyContent: "center" }}
                    >
                      <CircularProgress size={22} />
                    </Box>
                  ) : assets.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: "text.secondary",
                        textAlign: "center",
                        py: 1.5,
                      }}
                    >
                      尚無已上傳圖片 / No uploaded images yet
                    </Typography>
                  ) : (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: FONT_FAMILY,
                          color: "text.secondary",
                        }}
                      >
                        圖片 / Images
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={1} mt={0.5}>
                        {assets.map((a) => (
                          <Box
                            key={a.id}
                            sx={{
                              width: 110,
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 2,
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              component="img"
                              src={a.url}
                              alt={a.name}
                              sx={{
                                width: "100%",
                                height: 70,
                                objectFit: "cover",
                                display: "block",
                                cursor: "pointer",
                              }}
                              onClick={() => insertUploadedAssetIntoEditor(a)}
                            />
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{ px: 0.75, py: 0.5 }}
                            >
                              <Tooltip title="插入 / Insert">
                                <IconButton
                                  size="small"
                                  onClick={() => insertUploadedAssetIntoEditor(a)}
                                >
                                  <AddRoundedIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="刪除 / Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteAsset(a.id)}
                                >
                                  <DeleteRoundedIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Paper>
              </Box>

              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={resetEditor}
                  sx={{ fontFamily: FONT_FAMILY }}
                >
                  清除 / Reset
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                  sx={{ fontFamily: FONT_FAMILY, px: 4 }}
                >
                  {saving ? "儲存中..." : "儲存 / Save"}
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Stack>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4200}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ fontFamily: FONT_FAMILY }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
